import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Star, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

import { API } from "@/lib/apiBase";

const PLANS = [
  {
    id: "club",
    label: "Club",
    icon: Star,
    tagline: "For collections under 250 bottles",
    monthly: { id: "club_monthly", price: 25, suffix: "/mo" },
    yearly:  { id: "club_yearly",  price: 249, suffix: "/yr" },
    features: [
      "Full access to AlfredWine - Wine Collection Ratings",
      "Private Cellar up to 250 bottles",
      "100K+ wine reference database",
      "Market values",
      "Statistics",
      "Newsletter",
    ],
  },
  {
    id: "premium",
    label: "Premium",
    icon: Sparkles,
    featured: true,
    tagline: "For collections of up to 2 000 bottles",
    monthly: { id: "premium_monthly", price: 50,  suffix: "/mo" },
    yearly:  { id: "premium_yearly",  price: 498, suffix: "/yr" },
    features: [
      "All Club benefits",
      "Private Cellar up to 2 000 bottles",
      "Access to AlfredWine Marketplace",
      "Exclusive offers",
    ],
  },
  {
    id: "business",
    label: "Business",
    icon: Crown,
    tagline: "For unlimited bottles, marketplace",
    custom: true,
    features: [
      "All Premium benefits",
      "Offer products for sell at AlfredWine Marketplace",
      "Access to expert services",
    ],
  },
];

export default function Membership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interval, setInterval] = useState("yearly");
  const [loadingId, setLoadingId] = useState("");

  const subscribe = async (plan) => {
    if (!user) {
      toast.error("Please sign in to subscribe");
      navigate("/login", { state: { from: "/membership" } });
      return;
    }
    // Fall back to monthly for plans that don't have a yearly option
    const tier = interval === "monthly" ? plan.monthly : (plan.yearly || plan.monthly);
    if (!tier) return;
    setLoadingId(tier.id);
    try {
      const res = await axios.post(`${API}/membership/checkout`, {
        package_id: tier.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setLoadingId("");
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="membership-page">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-3">PRIVATE COLLECTORS</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white/95 mb-4">
            Membership Plans
          </h1>
          <p className="font-body text-base text-white/50 max-w-2xl mx-auto leading-relaxed">
            Whether you collect to invest or to enjoy, choose a plan that fits your collection.
          </p>
        </div>

        {/* Interval toggle */}
        <div className="flex justify-center mb-10">
          <Tabs value={interval} onValueChange={setInterval} className="w-auto">
            <TabsList className="bg-wine-paper border border-white/5 rounded-sm p-1">
              <TabsTrigger value="yearly" data-testid="interval-yearly" className="font-body text-sm data-[state=active]:bg-merlot/20 data-[state=active]:text-white px-6">
                Yearly · save up to 17%
              </TabsTrigger>
              <TabsTrigger value="monthly" data-testid="interval-monthly" className="font-body text-sm data-[state=active]:bg-merlot/20 data-[state=active]:text-white px-6">
                Monthly
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="membership-plans">
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              interval={interval}
              loading={
                loadingId ===
                (interval === "monthly"
                  ? plan.monthly?.id
                  : (plan.yearly?.id || plan.monthly?.id))
              }
              onSubscribe={() => subscribe(plan)}
            />
          ))}
        </div>

        <p className="text-center font-body text-xs text-white/30 mt-10">
          All payments are processed securely via Stripe.
        </p>
      </div>
    </main>
  );
}

function PlanCard({ plan, interval, loading, onSubscribe }) {
  const Icon = plan.icon;

  // Resolve the available tier given the toggle
  let effectiveTier = null;
  let onlyLabel = null;
  if (!plan.custom) {
    if (interval === "monthly") {
      effectiveTier = plan.monthly || plan.yearly;
      onlyLabel = !plan.monthly ? "Yearly only" : null;
    } else {
      effectiveTier = plan.yearly || plan.monthly;
      onlyLabel = !plan.yearly ? "Monthly only" : null;
    }
  }
  const featured = plan.featured;
  const buttonLabel = plan.custom
    ? null
    : `Subscribe ${effectiveTier?.suffix === "/yr" ? "Yearly" : "Monthly"}`;

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative glass-card rounded-sm p-7 flex flex-col ${
        featured ? "border-merlot/40 ring-1 ring-merlot/30" : ""
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-merlot px-3 py-1 rounded-sm font-accent text-[10px] tracking-[0.2em] text-white">
          MOST POPULAR
        </span>
      )}
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-5 h-5 ${featured ? "text-gold" : "text-merlot-light"}`} />
        <h3 className="font-display text-2xl text-white/90">{plan.label}</h3>
      </div>
      <p className="font-body text-xs text-white/40 mb-6">{plan.tagline}</p>

      <div className="mb-6 min-h-[64px]">
        {plan.custom ? (
          <div>
            <p className="font-display text-3xl text-white/90">Custom</p>
            <p className="font-body text-xs text-white/40">price upon request</p>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-4xl text-white/95">${effectiveTier.price}</span>
              <span className="font-body text-sm text-white/40">{effectiveTier.suffix}</span>
            </div>
            {interval === "yearly" && plan.monthly && plan.yearly && (
              <p
                data-testid={`plan-savings-${plan.id}`}
                className="font-body text-[11px] text-gold/80 mt-1 tracking-wide"
              >
                Save ${plan.monthly.price * 12 - plan.yearly.price}/yr vs monthly
              </p>
            )}
            {onlyLabel && (
              <p className="font-body text-[10px] text-gold/60 mt-1 tracking-wider uppercase">{onlyLabel}</p>
            )}
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 font-body text-sm text-white/70">
            <Check className="w-4 h-4 text-gold/70 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {plan.custom ? (
        <a
          href="mailto:tastings@alfredwines.com?subject=Business%20Membership%20Inquiry"
          data-testid="plan-custom-contact"
        >
          <Button variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10 h-11 gap-2 font-body text-sm">
            <Mail className="w-4 h-4" /> Request a Quote
          </Button>
        </a>
      ) : (
        <Button
          onClick={onSubscribe}
          disabled={loading}
          data-testid={`plan-subscribe-${plan.id}`}
          className={`w-full h-11 font-body text-sm ${featured ? "bg-merlot hover:bg-merlot-light text-white" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}
        >
          {loading ? "Redirecting…" : buttonLabel}
        </Button>
      )}
    </div>
  );
}
